import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Link,
  Container,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import FirstLoginPasswordChange from '../../components/auth/FirstLoginPasswordChange';
import MFAChallengeDialog from '../../components/auth/MFAChallengeDialog';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, signOut, completeNewPassword, respondToMFAChallenge, user, loading: authLoading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [showMFADialog, setShowMFADialog] = useState(false);
  const [mfaChallengeData, setMfaChallengeData] = useState<{ session: string; username?: string } | null>(null);

  // Check if user is already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Check for existing session error
  useEffect(() => {
    if (error && error.includes('already a signed in user')) {
      setHasExistingSession(true);
    }
  }, [error]);

  const handleSignOut = async () => {
    setLoading(true);
    setError('');
    try {
      await signOut();
      setHasExistingSession(false);
      setUsername('');
      setPassword('');
    } catch (err: any) {
      setError('Failed to sign out: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn(username, password);
      console.log('Sign in result:', result);
      
      if (result.success) {
        navigate('/');
      } else if (result.requiresPasswordChange) {
        // User needs to change their temporary password
        setShowPasswordChangeDialog(true);
        setError(''); // Clear error since this is expected
      } else if (result.requiresMFA) {
        console.log('MFA required, challengeData:', result.challengeData);
        // User needs to provide MFA token
        if (result.challengeData) {
          setMfaChallengeData({
            session: result.challengeData.Session,
            username: result.challengeData.ChallengeParameters?.USERNAME || result.challengeData.Username || username,
          });
          setShowMFADialog(true);
          setError(''); // Clear error since this is expected
        } else {
          console.error('MFA required but no challengeData provided');
          setError('MFA challenge data missing');
        }
      } else {
        setError(result.error || 'Failed to sign in');
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (newPassword: string) => {
    setLoading(true);
    setError('');

    try {
      const result = await completeNewPassword(newPassword);
      if (result.success) {
        setShowPasswordChangeDialog(false);
        // Navigate to home after successful password change
        navigate('/');
      } else {
        setError(result.error || 'Failed to change password');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerify = async (code: string, session: string, username?: string) => {
    setLoading(true);
    setError('');

    try {
      const result = await respondToMFAChallenge(code, session, username);
      if (result.success) {
        setShowMFADialog(false);
        setMfaChallengeData(null);
        // Navigate to home after successful MFA verification
        navigate('/');
      } else {
        setError(result.error || 'Invalid verification code');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <SportsTennisIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Blakeney Tennis Manager
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sign in to your account
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
                {hasExistingSession && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSignOut}
                      disabled={loading}
                    >
                      Sign out existing session
                    </Button>
                  </Box>
                )}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                margin="normal"
                required
                autoFocus
                disabled={loading}
                autoComplete="username"
                name="username"
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading}
                autoComplete="current-password"
                name="password"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  sx={{ cursor: 'pointer', mb: 2, display: 'block' }}
                  variant="body2"
                >
                  Forgot password?
                </Link>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate('/signup')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Sign up
                  </Link>
                </Typography>
              </Box>
            </form>
          </CardContent>
        </Card>

        {/* First Login Password Change Dialog */}
        <FirstLoginPasswordChange
          open={showPasswordChangeDialog}
          onPasswordChange={handlePasswordChange}
          loading={loading}
        />

        {/* MFA Challenge Dialog */}
        {mfaChallengeData && (
          <MFAChallengeDialog
            open={showMFADialog}
            onVerify={handleMFAVerify}
            session={mfaChallengeData.session}
            username={mfaChallengeData.username}
            loading={loading}
          />
        )}
      </Box>
    </Container>
  );
};

export default LoginPage;
